import React from "react";
import {Box, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {useTranslation} from "react-i18next";
import {useTheme} from "@mui/material/styles";
import {printoutPalette} from "../../../core/printoutPalette";
import {parseColorAttribute} from "../../../blocks/shared/blockAttributes";
import {resolveTableBorderColors} from "../../../blocks/table/printoutTableOptions";
import type {
    PrintoutTableBorderColors,
    PrintoutTableNodeAttrs,
} from "../../../blocks/table/printoutTableOptions";
import {PanelField, PanelHint} from "../PrintoutPanelSection";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

type DirectionalBorderColorAttribute = keyof Pick<
    PrintoutTableNodeAttrs,
    "borderTopColor" | "borderRightColor" | "borderBottomColor" | "borderLeftColor"
>;

const BORDER_COLOR_FIELDS: readonly {
    attribute: DirectionalBorderColorAttribute;
    label: string;
    side: keyof PrintoutTableBorderColors;
}[] = [
    {attribute: "borderTopColor", label: "Top", side: "top"},
    {attribute: "borderRightColor", label: "Right", side: "right"},
    {attribute: "borderBottomColor", label: "Bottom", side: "bottom"},
    {attribute: "borderLeftColor", label: "Left", side: "left"},
];

export function TableSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const theme = useTheme();

    const attrs = block.node.attrs as PrintoutTableNodeAttrs;
    const backgroundColor = parseColorAttribute(attrs.backgroundColor) ?? 'transparent';
    const borderColors = resolveTableBorderColors(attrs);
    const fillColorPresets = [
        'transparent',
        printoutPalette.paper,
        printoutPalette.subtleFill,
        theme.palette.primary.main,
    ];
    const borderColorPresets = [
        'transparent',
        printoutPalette.subtleBorder,
        printoutPalette.mutedText,
        printoutPalette.primaryText,
        theme.palette.primary.main,
    ];

    const update = (next: Partial<PrintoutTableNodeAttrs>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, next);

    const updateBorderColor = (
        attribute: DirectionalBorderColorAttribute,
        color?: string,
    ) => update({
        borderTopColor: borderColors.top,
        borderRightColor: borderColors.right,
        borderBottomColor: borderColors.bottom,
        borderLeftColor: borderColors.left,
        [attribute]: color ?? "transparent",
    });

    return (
        <>
            <PanelField label={t('Fill')}>
                <NewColorPicker
                    disabled={disabled}
                    id={'printout-table-fill-color'}
                    onChange={(value) => update({backgroundColor: value ?? 'transparent'})}
                    presetColors={fillColorPresets}
                    value={backgroundColor}
                />
            </PanelField>

            <PanelField label={t('Borders')}>
                <Stack gap={1}>
                    {BORDER_COLOR_FIELDS.map(({attribute, label, side}) => (
                        <Stack alignItems={'center'} direction={'row'} gap={1} key={side}>
                            <Typography
                                color={'text.secondary'}
                                sx={{flexShrink: 0, width: 52}}
                                variant={'body2'}
                            >
                                {t(label)}
                            </Typography>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <NewColorPicker
                                    disabled={disabled}
                                    id={`printout-table-border-${side}-color`}
                                    onChange={(value) => updateBorderColor(attribute, value)}
                                    placeholder={t('Transparent')}
                                    presetColors={borderColorPresets}
                                    value={borderColors[side]}
                                />
                            </Box>
                        </Stack>
                    ))}
                </Stack>
            </PanelField>

            <PanelHint>
                {t('Select a cell to add or remove rows and columns.')}
            </PanelHint>
        </>
    );
}

export default TableSettings;
